import "./utilities_namespace.js";
import "./publishing_domain.js";

// Locale forwarding for embedded experiences.
//
// Embedded pages run on a host CMS origin. The embed derives the desired locale
// from the host page itself and forwards it as an explicit `?locale=` param on
// Sparkle-bound requests. An explicit `?locale=` is also persisted as a first-party cookie
// ON the host domain: host-CMS pages render their own links (built from synced
// data) that no Sparkle script can decorate, so the cookie — not the links —
// is what carries the language across host-page navigation; each page's
// embeds re-read it. The server validates every forwarded param (see
// Concerns::CurrentLocale), and the param keeps Sparkle's own page cache safe
// (`$is_args` bypass + the non-default write guard).
//
// A relative link or request URL is resolved against the document base, as the browser resolves it: a host
// CMS layout preview's base is the host's server. The page's own address is read from `window.location`.

// The client-accepted list (Sparkle::Defaults), not I18n.available_locales.
Sparkle.Embed.Utilities.AVAILABLE_LOCALES = window.SparkleConfig?.locales?.available ?? [];
Sparkle.Embed.Utilities.DEFAULT_LOCALE = window.SparkleConfig?.locales?.default ?? "en";
// locale → og:locale code, the same map the server renders into its own heads.
Sparkle.Embed.Utilities.OG_LOCALES = window.SparkleConfig?.locales?.og ?? {};

// Returns the non-default locale `url`'s path starts with, matched exactly as the server matches it, or null:
// "/es/physicians/jane-doe" → "es".
Sparkle.Embed.Utilities.pathLocale = function (url) {
  let segment;
  try {
    segment = new URL(url, document.baseURI).pathname.split("/")[1];
  } catch {
    return null;
  }

  const { AVAILABLE_LOCALES, DEFAULT_LOCALE } = Sparkle.Embed.Utilities;

  return segment !== DEFAULT_LOCALE && AVAILABLE_LOCALES.includes(segment) ? segment : null;
};

// Returns the locale `url` names, as a path prefix or as a `locale` param the client exposes, or null:
// "/es/physicians/jane-doe" → "es", "/physicians/jane-doe?locale=en" → "en". A link that names a locale
// either way is addressed to it rather than to the page's.
Sparkle.Embed.Utilities.urlLocale = function (url) {
  const pathLocale = Sparkle.Embed.Utilities.pathLocale(url);
  if (pathLocale) {
    return pathLocale;
  }

  let param;
  try {
    param = new URL(url, document.baseURI).searchParams.get("locale");
  } catch {
    return null;
  }

  return Sparkle.Embed.Utilities.AVAILABLE_LOCALES.includes(param) ? param : null;
};

// Returns whether the page is on a host CMS that takes the locale in its path (`detectEmbeddingUrlLocale`).
Sparkle.Embed.Utilities.hostTakesPathLocale = function () {
  return !Sparkle.Embed.Utilities.isSparkleDomain() && Sparkle.Embed.Utilities.detectEmbeddingUrlLocale() === "path";
};

(function () {
  // Named to avoid colliding with anything the host CMS calls `locale`.
  const LOCALE_COOKIE = "sparkle_locale";
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

  function normalizeLocale(candidate) {
    if (!candidate) {
      return null;
    }

    const candidateLower = candidate.toLowerCase();

    return Sparkle.Embed.Utilities.AVAILABLE_LOCALES.find((locale) => locale.toLowerCase() === candidateLower) || null;
  }

  function localeFromUrl() {
    try {
      const param = new URLSearchParams(window.location.search).get("locale") || "";
      return normalizeLocale(param) || normalizeLocale(param.split("-")[0]);
    } catch {
      return null;
    }
  }

  // The page's locale path prefix on a Sparkle page: `/es/physicians/jane-doe` → "es". Known before any
  // deferred page script publishes `window.Sparkle.locale`. A host CMS page's own paths are not read here.
  function localeFromSparklePath() {
    return Sparkle.Embed.Utilities.isSparkleDomain() ? Sparkle.Embed.Utilities.pathLocale(window.location.href) : null;
  }

  function localeFromSparkle() {
    // Published by the server on Sparkle-rendered pages (sparkle_locale_js) —
    // authoritative there, absent on host-CMS pages.
    return normalizeLocale(window.Sparkle && window.Sparkle.locale);
  }

  function localeFromCookie() {
    const match = document.cookie.match(/(?:^|;\s*)sparkle_locale=([^;]*)/);
    return match ? normalizeLocale(decodeURIComponent(match[1])) : null;
  }

  function localeFromDocument() {
    const lang = document.documentElement.lang || "";
    return normalizeLocale(lang) || normalizeLocale(lang.split("-")[0]);
  }

  // The locale the embedded experience should render in. On a host CMS that
  // takes the locale in its path, the path alone decides: `/es/…` is Spanish and
  // a bare path the default locale. Elsewhere the precedence is the page's
  // `?locale=` param, a Sparkle page's locale path prefix, the server-published
  // locale (Sparkle-rendered pages), the host-domain cookie (persisted from an
  // earlier explicit param), then the host page's `<html lang>` (exact match,
  // else its primary language subtag). Unknown values are ignored and the
  // default locale resolves to null, so default-locale URLs stay param-free and
  // page-cacheable.
  Sparkle.Embed.Utilities.currentLocale = function () {
    if (Sparkle.Embed.Utilities.hostTakesPathLocale()) {
      return Sparkle.Embed.Utilities.pathLocale(window.location.href);
    }

    const resolved =
      localeFromUrl() || localeFromSparklePath() || localeFromSparkle() || localeFromCookie() || localeFromDocument();

    return resolved && resolved !== Sparkle.Embed.Utilities.DEFAULT_LOCALE ? resolved : null;
  };

  // Persist an explicit `?locale=` onto the host domain: a non-default choice
  // sticks, choosing the default clears it. Runs at bundle load on every page
  // that carries an embed. A host that takes the locale in its path is never
  // given the cookie, since `currentLocale` does not read it there.
  Sparkle.Embed.Utilities.persistLocale = function () {
    const requested = localeFromUrl();

    if (!requested || Sparkle.Embed.Utilities.hostTakesPathLocale()) {
      return;
    }

    try {
      if (requested === Sparkle.Embed.Utilities.DEFAULT_LOCALE) {
        document.cookie = LOCALE_COOKIE + "=; path=/; max-age=0; SameSite=Lax";
      } else {
        document.cookie =
          LOCALE_COOKIE +
          "=" +
          encodeURIComponent(requested) +
          "; path=/; max-age=" +
          COOKIE_MAX_AGE +
          "; SameSite=Lax";
      }
    } catch {
      // Cookie writes can fail in exotic embedding contexts; the param keeps working.
    }
  };

  // Mirror the resolved locale onto the host document: `<html lang>` and the
  // `og:locale` meta. A host-CMS page renders its own head in its own default
  // language, so without this, in-browser inspection (and crawlers that execute
  // JS) report the wrong language for a localized embed. The page's static
  // source still carries the host CMS's values — only the host CMS can change
  // what it serves.
  Sparkle.Embed.Utilities.localizeHostDocument = function () {
    const locale = Sparkle.Embed.Utilities.currentLocale();

    if (!locale) {
      return;
    }

    document.documentElement.lang = locale;

    const ogLocale = Sparkle.Embed.Utilities.OG_LOCALES[locale];

    if (!ogLocale) {
      return;
    }

    let meta = document.querySelector('meta[property="og:locale"]');

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", "og:locale");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", ogLocale);
  };

  Sparkle.Embed.Utilities.persistLocale();
  Sparkle.Embed.Utilities.localizeHostDocument();
})();

// Forward the resolved locale on a URL that is Sparkle-bound by construction
// (remote-content and embed-config fetches), so the server renders that locale.
// A URL that already names a locale (`urlLocale`) is left as it is.
Sparkle.Embed.Utilities.localizeUrl = function (url) {
  const locale = Sparkle.Embed.Utilities.currentLocale();

  if (!locale || Sparkle.Embed.Utilities.urlLocale(url)) {
    return url;
  }

  return Sparkle.Embed.Utilities.modifyUrlParams(url, { locale: locale });
};

// Extensions a host CMS serves as files rather than pages, which have no copy under a locale prefix.
const FILE_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
  "txt",
  "xml",
  "zip",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "svg",
  "webp",
  "mp3",
  "mp4",
  "ics",
  "vcf",
]);

// Forward the resolved locale on a navigation link. Applies to Sparkle-bound
// URLs (so the server renders that locale) and to same-host links (so a
// param-carrying entry link hands the locale to the next host page even before
// the cookie exists); third-party URLs are left untouched. A same-host page link
// on a host that takes the locale in its path gets the path prefix instead
// (`localizeHostUrl`), also when it names its locale as a param, which the next
// host page would not read; a same-host file link there (`FILE_EXTENSIONS`) is
// left as it is. A link that already names a locale (`urlLocale`) keeps it.
Sparkle.Embed.Utilities.injectUrlWithLocale = function (url) {
  if (Sparkle.Embed.Utilities.isSparkleDomain(url) || isSparkleApiHostUrl(url)) {
    return Sparkle.Embed.Utilities.localizeUrl(url);
  }

  if (!isHostPageUrl(url)) {
    return url;
  }

  if (!Sparkle.Embed.Utilities.hostTakesPathLocale()) {
    return Sparkle.Embed.Utilities.localizeUrl(url);
  }

  if (isFileUrl(url) || Sparkle.Embed.Utilities.pathLocale(url)) {
    return url;
  }

  return Sparkle.Embed.Utilities.localizeHostUrl(
    url,
    Sparkle.Embed.Utilities.urlLocale(url) || Sparkle.Embed.Utilities.currentLocale(),
  );

  function isFileUrl(candidate) {
    try {
      const extension = new URL(candidate, document.baseURI).pathname.split("/").pop().split(".").pop();

      return FILE_EXTENSIONS.has(extension.toLowerCase());
    } catch {
      return false;
    }
  }

  function isSparkleApiHostUrl(candidate) {
    try {
      return (
        !!window.Sparkle.apiHost &&
        new URL(candidate, document.baseURI).origin === new URL(window.Sparkle.apiHost).origin
      );
    } catch {
      return false;
    }
  }

  function isHostPageUrl(candidate) {
    try {
      return new URL(candidate, document.baseURI).host === window.location.host;
    } catch {
      return false;
    }
  }
};

// Returns `url` without its locale path prefix, relative if it was relative:
// "/es/physicians/jane-doe?page=2" → "/physicians/jane-doe?page=2".
Sparkle.Embed.Utilities.stripLocalePrefix = function (url) {
  const locale = Sparkle.Embed.Utilities.pathLocale(url);
  if (!locale) {
    return url;
  }

  const parsed = new URL(url, document.baseURI);
  parsed.pathname = parsed.pathname.slice(locale.length + 1) || "/";

  return serializeLike(url, parsed);
};

// Returns the host CMS `url` addressed to `locale` the way the host page takes it (see
// `detectEmbeddingUrlLocale`): a path prefix, "https://host.org/es/physician-locator/jane-doe", or a
// param, "https://host.org/physician-locator/jane-doe?locale=es". A locale `url` already names that way
// is replaced, and under a prefix a `locale` param gives way to it. A null or default `locale` returns
// `url` as given.
Sparkle.Embed.Utilities.localizeHostUrl = function (url, locale) {
  if (!locale || locale === Sparkle.Embed.Utilities.DEFAULT_LOCALE) {
    return url;
  }

  if (Sparkle.Embed.Utilities.detectEmbeddingUrlLocale() !== "path") {
    return Sparkle.Embed.Utilities.modifyUrlParams(url, { locale: locale });
  }

  const parsed = new URL(Sparkle.Embed.Utilities.stripLocalePrefix(url), document.baseURI);
  parsed.pathname = `/${locale}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  parsed.searchParams.delete("locale");

  return serializeLike(url, parsed);
};

// Serializes `parsed` as an absolute url when `original` was one, else as a path with its query and hash.
function serializeLike(original, parsed) {
  if (/^([a-z][a-z0-9+.-]*:)?\/\//i.test(original)) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
